import { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useAppState } from '../context/AppStateContext';
import { MALE_AVATARS, FEMALE_AVATARS } from '../utils/avatar';
import { registerDevice } from '../services/pushNotifications';
import { getCountryCode } from '../utils/currency';

// Use a diverse set of avatars for the orbiting animation (2 male, 2 female)
const ORBITING_AVATARS = [
  MALE_AVATARS[0],   // Male-memoji-5
  FEMALE_AVATARS[0], // Female-memoji-1
  MALE_AVATARS[1],   // Male-memoji-6
  FEMALE_AVATARS[1], // Female-memoji-2
];

// Confetti piece component
function ConfettiPiece({ delay, left, color }) {
  return (
    <div
      className="absolute w-3 h-3 rounded-sm animate-confetti"
      style={{
        left: `${left}%`,
        backgroundColor: color,
        animationDelay: `${delay}ms`,
        top: '-10px',
      }}
    />
  );
}

export default function SignUpSuccessPage() {
  const navigate = useNavigate();
  const { enterApp, userProfile, user, fetchUserData } = useAuth();
  const { profileForm, setProfileForm } = useAppState();
  const [showContent, setShowContent] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const hasFetchedRef = useRef(false);

  // Get firstName from multiple sources with fallbacks
  // Priority: fetched user data > userProfile (signup flow) > profileForm
  const firstName = user?.first_name || user?.firstName
    || userProfile?.firstName
    || profileForm?.firstName
    || '';

  useEffect(() => {
    // Trigger content fade-in
    setTimeout(() => setShowContent(true), 100);
  }, []);

  // Prefetch user data (including rewards balance with signup bonus) when page mounts
  // This ensures the home page hero card shows the updated balance immediately
  useEffect(() => {
    if (hasFetchedRef.current) return;
    hasFetchedRef.current = true;

    // Fetch user data to get the signup bonus in the rewards balance
    fetchUserData().then((userData) => {
      console.log('[SignUpSuccessPage] Prefetched user data with signup bonus:', userData?.profile?.first_name);

      // Sync profileForm with fetched user data for fallback display
      if (userData?.profile) {
        const profile = userData.profile;
        setProfileForm((prev) => ({
          ...prev,
          firstName: profile.first_name || profile.firstName || prev.firstName || '',
          lastName: profile.last_name || profile.lastName || prev.lastName || '',
          email: profile.email || prev.email || '',
          phone: profile.phone || profile.phone_number || prev.phone || '',
          country: getCountryCode(profile.country) || prev.country || '',
          bonndleTag: profile.bold_tag || profile.boldTag || prev.bonndleTag || '',
        }));
      }
    }).catch((err) => {
      console.warn('[SignUpSuccessPage] Failed to prefetch user data:', err);
    }).finally(() => {
      setIsLoading(false);
    });

    // Register device for push notifications (non-blocking)
    registerDevice().catch((err) => {
      console.warn('[SignUpSuccessPage] Failed to register device:', err);
    });
  }, [fetchUserData, setProfileForm]);

  const handleGetStarted = () => {
    enterApp();
    navigate('/home');
  };

  const handleCompleteProfile = () => {
    // Route to identity verification first, then profile
    navigate('/verify-identity');
  };

  // Generate confetti pieces
  const confettiColors = ['#4F46E5', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899'];
  const confettiPieces = Array.from({ length: 50 }, (_, i) => ({
    id: i,
    left: Math.random() * 100,
    delay: Math.random() * 1000,
    color: confettiColors[Math.floor(Math.random() * confettiColors.length)],
  }));

  return (
    <div className="min-h-screen min-h-dvh flex flex-col bg-white overflow-hidden relative">
      {/* Confetti Container */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        {confettiPieces.map((piece) => (
          <ConfettiPiece
            key={piece.id}
            left={piece.left}
            delay={piece.delay}
            color={piece.color}
          />
        ))}
      </div>

      {/* Main Content */}
      <div className={`flex-1 flex flex-col items-center justify-center px-6 transition-all duration-700 ${showContent ? 'opacity-100 scale-100' : 'opacity-0 scale-95'}`}>
        {/* Logo with Orbiting Memojis */}
        <div className="relative w-48 h-48 mb-8">
          {/* Orbit Path with Gradient */}
          <div
            className="absolute inset-0 rounded-full animate-orbit-ring"
            style={{
              background: 'conic-gradient(from 0deg, #4F46E5, #8B5CF6, #EC4899, #F59E0B, #10B981, #4F46E5)',
              padding: '3px',
              WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
              WebkitMaskComposite: 'xor',
              maskComposite: 'exclude',
            }}
          />

          {/* Center Logo */}
          <div className="absolute inset-0 flex items-center justify-center">
            <img
              src="/logo/bonndleLogoIcon_Indigo.svg"
              alt="Bonndle"
              className="w-16 h-16"
            />
          </div>

          {/* Orbiting Memojis - mix of male and female avatars */}
          {ORBITING_AVATARS.map((avatarSrc, index) => (
            <div
              key={index}
              className="absolute inset-0 animate-orbit"
              style={{
                animationDelay: `${index * -2}s`,
                animationDuration: '8s',
              }}
            >
              <div
                className="absolute animate-counter-orbit"
                style={{
                  top: '0%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  animationDelay: `${index * -2}s`,
                  animationDuration: '8s',
                }}
              >
                <img
                  src={avatarSrc}
                  alt=""
                  className="w-14 h-14 rounded-full shadow-lg border-2 border-white object-cover bg-white"
                />
              </div>
            </div>
          ))}
        </div>

        {/* Welcome Text */}
        <h1 className="text-2xl font-bold text-gray-900 mb-2 text-center">
          Welcome to Bonndle{firstName ? `, ${firstName}` : ''}!
        </h1>
        <p className="text-gray-600 text-center mb-8">
          You've earned a <span className="font-bold text-emerald-600">$2</span> sign-up reward.
          <br />
          Complete your profile to unlock it.
        </p>

        {/* Action Buttons */}
        <div className="w-full max-w-xs space-y-3">
          <button
            onClick={handleCompleteProfile}
            className="w-full px-8 py-4 bg-indigo-600 text-white font-bold text-lg rounded-2xl hover:bg-indigo-700 active:scale-95 transition-all shadow-lg"
          >
            Complete Your Profile
          </button>
          <button
            onClick={handleGetStarted}
            className="w-full px-8 py-4 bg-gray-100 text-gray-700 font-semibold text-lg rounded-2xl hover:bg-gray-200 active:scale-95 transition-all"
          >
            Skip for Now
          </button>
        </div>
      </div>

      {/* CSS Animations */}
      <style>{`
        @keyframes orbit {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }

        @keyframes counterOrbit {
          from {
            transform: translate(-50%, -50%) rotate(0deg);
          }
          to {
            transform: translate(-50%, -50%) rotate(-360deg);
          }
        }

        @keyframes confetti {
          0% {
            transform: translateY(0) rotate(0deg);
            opacity: 1;
          }
          100% {
            transform: translateY(100vh) rotate(720deg);
            opacity: 0;
          }
        }

        .animate-orbit {
          animation: orbit 8s linear infinite;
          transform-origin: center center;
        }

        .animate-orbit-ring {
          animation: orbit 4s linear infinite;
        }

        .animate-counter-orbit {
          animation: counterOrbit 8s linear infinite;
        }

        .animate-confetti {
          animation: confetti 3s ease-out forwards;
        }
      `}</style>
    </div>
  );
}
